import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Settings as SettingsIcon, Hash, CheckCircle2, XCircle, Loader2, ExternalLink } from 'lucide-react';
import { EmptyState, cn } from '@/components/ui';

/*
  Settings — корневая страница настроек.
  Секции:
    - Подключения (user-level OAuth: Slack, future Google/Jira/...)
    - Уведомления (TODO)
    - Профиль (synced from Google Workspace, edit later)

  Sprint Iter-1.5-C (2026-05-24): добавлена секция «Подключения» с Slack OAuth.
*/

interface SlackStatus {
  linked: boolean;
  scopes?: string[];
  linked_at?: string;
  login_url?: string;
}

export function Settings() {
  const [params] = useSearchParams();
  const slackOAuth = params.get('slack_oauth');
  const slackOAuthError = params.get('slack_oauth_error');

  return (
    <div className="mx-auto max-w-4xl px-8 py-10">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Настройки</h1>
        <p className="mt-1 text-sm text-fg-muted">Подключения, уведомления, профиль.</p>
      </header>

      {/* OAuth callback flash messages */}
      {slackOAuth === 'ok' && <FlashBanner kind="success">Slack подключён ✓</FlashBanner>}
      {slackOAuthError && <FlashBanner kind="error">Ошибка Slack OAuth: {slackOAuthError}</FlashBanner>}

      <Section
        title="Подключения"
        subtitle="OAuth-привязки к внешним сервисам — для приватности и действий от твоего имени."
      >
        <div className="flex flex-col gap-3">
          <SlackIntegration />
        </div>
      </Section>

      <Section title="Уведомления" subtitle="Канал доставки и приоритеты — появится вместе с подписками.">
        <EmptyState
          icon={<SettingsIcon />}
          title="Раздел в разработке"
          description="Подписки на события сервисов и каналы доставки появятся позже."
        />
      </Section>
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-10">
      <header className="mb-3">
        <h2 className="text-xs font-semibold tracking-wider text-fg-subtle uppercase">{title}</h2>
        <p className="mt-0.5 text-sm text-fg-muted">{subtitle}</p>
      </header>
      {children}
    </section>
  );
}

function FlashBanner({ kind, children }: { kind: 'success' | 'error'; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'mb-4 flex items-center gap-2 rounded-md border px-3 py-2 text-sm',
        kind === 'success' ? 'notice-success' : 'notice-error',
      )}
    >
      {kind === 'success' ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
      {children}
    </div>
  );
}

// ----- Slack integration card -----

async function readSlackStatus(): Promise<SlackStatus> {
  const resp = await fetch('/auth/slack/status', { credentials: 'include' });
  return resp.ok ? ((await resp.json()) as SlackStatus) : { linked: false, login_url: '/auth/slack/login' };
}

function SlackIntegration() {
  const [status, setStatus] = useState<SlackStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);

  async function load() {
    setLoading(true);
    try {
      setStatus(await readSlackStatus());
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void readSlackStatus()
      .then(setStatus)
      .finally(() => setLoading(false));
  }, []);

  async function disconnect() {
    if (!confirm('Отвязать Slack? Действия от твоего имени в Slack станут недоступны.')) {
      return;
    }
    setDisconnecting(true);
    try {
      const resp = await fetch('/auth/slack/disconnect', {
        method: 'POST',
        credentials: 'include',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      await load(); // refresh status
    } catch (err) {
      alert('Ошибка отвязки: ' + String(err));
    } finally {
      setDisconnecting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-line bg-card p-4 text-sm text-fg-subtle">
        <Loader2 size={16} className="animate-spin" />
        Проверяю статус…
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 rounded-lg border border-line bg-card p-4">
      <span className="grid size-10 shrink-0 place-items-center rounded-lg service-available">
        <Hash size={20} />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-fg">Slack</h3>
          {status?.linked ? (
            // Sprint 2026-05-25: gradient pill (как Portfolio «это ты»).
            // emerald-100/emerald-700 был слабый контраст и сливался.
            <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-br from-service-enabled-highlight to-service-enabled-text px-2 py-0.5 text-2xs font-semibold tracking-wide text-white uppercase shadow-success-tag">
              <CheckCircle2 size={10} />
              Подключён
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-line-strong bg-card px-2 py-0.5 text-2xs font-medium tracking-wide text-fg-muted uppercase">
              Не подключён
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-fg-muted">
          {status?.linked
            ? 'Аккаунт Slack связан с твоей учётной записью. Действия в Slack идут от твоего имени.'
            : 'Привяжи аккаунт, чтобы сервисы платформы знали тебя в Slack и могли действовать от твоего имени.'}
        </p>
        {status?.linked && status.scopes && status.scopes.length > 0 && (
          <p className="mt-1 text-2xs text-fg-subtle">Доступы: {status.scopes.join(', ')}</p>
        )}
      </div>

      <div className="shrink-0">
        {status?.linked ? (
          <button
            type="button"
            onClick={disconnect}
            disabled={disconnecting}
            className="inline-flex items-center gap-1.5 rounded-md border border-line-strong px-3 py-1.5 text-xs font-medium text-fg-muted transition-colors hover:bg-action-hover disabled:opacity-50"
          >
            {disconnecting ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
            Отвязать
          </button>
        ) : (
          <a
            href={status?.login_url ?? '/auth/slack/login'}
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-brand-accent to-brand-action-pressed px-3 py-1.5 text-xs font-medium text-white shadow-brand-action transition-opacity hover:opacity-90"
          >
            <ExternalLink size={12} />
            Привязать
          </a>
        )}
      </div>
    </div>
  );
}

export function SettingsProfile() {
  return (
    <div className="mx-auto max-w-4xl px-8 py-10">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Профиль</h1>
        <p className="mt-1 text-sm text-fg-muted">Имя, аватар, контактная информация.</p>
      </header>

      <EmptyState
        icon={<SettingsIcon />}
        title="Профиль из Google Workspace"
        description="Имя и аватар синхронизируются с Google Workspace. Редактирование появится в Sprint 4."
      />
    </div>
  );
}
